import { ITile, PipValue, BoardEnd, IBoardTile } from './types';

/**
 * Pure logic class for domino rules.
 */
export class DominoRules {
  /**
   * Generates a standard double-6 domino set.
   * @returns Array of 28 tiles
   */
  public static createTileSet(): ITile[] {
    const tiles: ITile[] = [];
    for (let i = 0; i <= 6; i++) {
      for (let j = i; j <= 6; j++) {
        tiles.push({
          a: i as PipValue,
          b: j as PipValue,
          id: `${i}_${j}`
        });
      }
    }
    return tiles;
  }

  /**
   * Shuffles an array of tiles using Fisher-Yates algorithm.
   * @param tiles Array of tiles to shuffle
   * @returns A new shuffled array
   */
  public static shuffleTiles(tiles: ITile[]): ITile[] {
    const shuffled = [...tiles];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Deals tiles to a specified number of players.
   * @param tiles The deck of tiles
   * @param numPlayers Number of players
   * @param tilesPerPlayer Tiles per player (default 7)
   * @returns Hands and remaining tiles
   */
  public static dealTiles(tiles: ITile[], numPlayers: number, tilesPerPlayer: number = 7): { hands: ITile[][]; remaining: ITile[] } {
    const deck = [...tiles];
    const hands: ITile[][] = [];
    for (let p = 0; p < numPlayers; p++) {
      const hand: ITile[] = [];
      for (let t = 0; t < tilesPerPlayer; t++) {
        if (deck.length > 0) {
          hand.push(deck.pop()!);
        }
      }
      hands.push(hand);
    }
    return { hands, remaining: deck };
  }

  /**
   * Checks if a tile can be played on either end.
   */
  public static canPlayTile(tile: ITile, leftEnd: PipValue | null, rightEnd: PipValue | null): boolean {
    if (leftEnd === null && rightEnd === null) return true;
    return tile.a === leftEnd || tile.b === leftEnd || tile.a === rightEnd || tile.b === rightEnd;
  }

  /**
   * Gets the ends a tile can be played on.
   */
  public static getPlayableEnd(tile: ITile, leftEnd: PipValue | null, rightEnd: PipValue | null): BoardEnd[] {
    if (leftEnd === null && rightEnd === null) return ['left', 'right'];
    const ends: BoardEnd[] = [];
    if (tile.a === leftEnd || tile.b === leftEnd) ends.push('left');
    if (tile.a === rightEnd || tile.b === rightEnd) ends.push('right');
    return ends;
  }

  /**
   * Orients a tile to match the board end.
   */
  public static orientTile(tile: ITile, end: BoardEnd, endValue: PipValue | null): IBoardTile {
    if (endValue === null) {
      return { tile, leftValue: tile.a, rightValue: tile.b };
    }

    let leftValue: PipValue;
    let rightValue: PipValue;

    if (end === 'left') {
      if (tile.b === endValue) {
        leftValue = tile.a;
        rightValue = tile.b;
      } else {
        leftValue = tile.b;
        rightValue = tile.a;
      }
    } else { // right end
      if (tile.a === endValue) {
        leftValue = tile.a;
        rightValue = tile.b;
      } else {
        leftValue = tile.b;
        rightValue = tile.a;
      }
    }

    return { tile, leftValue, rightValue };
  }

  /**
   * Checks if any tile in hand can be played.
   */
  public static hasPlayableTile(hand: ITile[], leftEnd: PipValue | null, rightEnd: PipValue | null): boolean {
    return hand.some(t => this.canPlayTile(t, leftEnd, rightEnd));
  }

  /**
   * Returns playable tiles.
   */
  public static getPlayableTiles(hand: ITile[], leftEnd: PipValue | null, rightEnd: PipValue | null): ITile[] {
    return hand.filter(t => this.canPlayTile(t, leftEnd, rightEnd));
  }

  /**
   * Calculates sum of all pips in a hand.
   */
  public static calculateRemainingPips(hand: ITile[]): number {
    return hand.reduce((sum, tile) => sum + tile.a + tile.b, 0);
  }

  /**
   * Checks if tile is a double.
   */
  public static isDouble(tile: ITile): boolean {
    return tile.a === tile.b;
  }

  /**
   * Finds the highest double in a hand.
   */
  public static findHighestDouble(hand: ITile[]): ITile | null {
    let highest: ITile | null = null;
    let highestVal = -1;
    for (const tile of hand) {
      if (this.isDouble(tile) && tile.a > highestVal) {
        highest = tile;
        highestVal = tile.a;
      }
    }
    return highest;
  }
}
